import { useState, useEffect } from 'react';
import WhatsAppOrderModal from './components/WhatsAppOrderModal';
import { createClient } from '@supabase/supabase-js';

interface SiteImage {
  id: string;
  section: string;
  image_key: string;
  image_url: string;
  alt_text: string;
}

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [images, setImages] = useState<{ [key: string]: string }>();
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const slides = [
    {
      title: '¡Agua pura para tu familia!',
      subtitle: '#AguaPureza',
      imageKey: 'hero-slide1'
    },
    {
      title: '¡Calidad y frescura garantizada!',
      subtitle: '#CalidadPureza',
      imageKey: 'hero-slide2'
    }
  ];

  const purificationSteps = [
    {
      title: 'Recepción de Agua de Lluvia',
      description: 'Captamos agua de lluvia de alta calidad como materia prima inicial para nuestro proceso de purificación.',
      icon: 'ri-cloud-line'
    },
    {
      title: 'Almacenamiento con Cloro',
      description: 'El agua se almacena en tanques herméticos donde se aplica cloro para desinfección inicial y control bacteriano.',
      icon: 'ri-archive-drawer-line'
    },
    {
      title: 'Remoción de Sedimentos',
      description: 'Eliminación de partículas sólidas, sedimentos gruesos y materia en suspensión mediante decantación y sedimentación.',
      icon: 'ri-stack-line'
    },
    {
      title: 'Filtración de Arena',
      description: 'Paso por filtros de arena que retienen partículas finas, turbidez y residuos no eliminados en la etapa anterior.',
      icon: 'ri-filter-2-line'
    },
    {
      title: 'Filtración Zeolita',
      description: 'Filtración con zeolita natural que absorbe amoníaco, metales pesados y mejora el sabor y olor del agua.',
      icon: 'ri-filter-line'
    },
    {
      title: 'Filtración Carbón Activado',
      description: 'Eliminación de cloro residual, compuestos orgánicos, olores y sabores no deseados con carbón activado de alto rendimiento.',
      icon: 'ri-filter-3-line'
    },
    {
      title: 'Filtración de 10, 5 y 1 Micras',
      description: 'Triple filtración por membranas de polipropileno que eliminan partículas de hasta 1 micra de tamaño.',
      icon: 'ri-fingerprint-line'
    },
    {
      title: 'Ósmosis Inversa',
      description: 'Proceso de membrana semipermeable de alta presión que elimina hasta el 99% de contaminantes, sales, bacterias y virus.',
      icon: 'ri-water-flash-line'
    },
    {
      title: 'Luz Ultravioleta',
      description: 'Desinfección final con lámpara UV-C que garantiza la eliminación total de microorganismos sin agregar químicos.',
      icon: 'ri-flashlight-line'
    },
    {
      title: 'Dosificación de Cloro Residual',
      description: 'Ajuste final de cloro residual a 0.3 mg/L ppm para garantizar inocuidad durante el almacenamiento y distribución.',
      icon: 'ri-test-tube-line'
    },
    {
      title: 'Envasado y Etiquetado',
      description: 'El agua purificada se envasa en botellones de alta calidad, sellados herméticamente y etiquetados para su distribución.',
      icon: 'ri-drop-line'
    }
  ];

  useEffect(() => {
    // Register site visit (once per session)
    const registerVisit = async () => {
      if (sessionStorage.getItem('visit_registered')) return;
      try {
        await supabase.from('site_visits').insert({
          page: '/',
          ip_address: '',
          user_agent: navigator.userAgent
        });
        sessionStorage.setItem('visit_registered', 'true');
      } catch (e) {
        console.error('Error registering visit:', e);
      }
    };
    registerVisit();
  }, []);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const response = await fetch('https://qdnmvfldkrvckefmtxhj.supabase.co/functions/v1/get-site-images');
      const result = await response.json();
      if (result.success) {
        const imageMap: { [key: string]: string } = {};
        result.data.forEach((img: SiteImage) => {
          imageMap[`${img.section}-${img.image_key}`] = img.image_url;
        });
        setImages(imageMap);
      }
    } catch (error) {
      console.error('Error al cargar imágenes:', error);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (showVideoModal) {
      timer = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % purificationSteps.length);
      }, 4000);
    }
    return () => clearInterval(timer);
  }, [showVideoModal, purificationSteps.length]);

  return (
    <div className="min-h-screen bg-white">
      {/* Encabezado Superior */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-xs sm:text-sm gap-2">
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-line"></i>
            <span className="text-center sm:text-left">Servicio de venta de agua purificada en botellones a domicilio</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform cursor-pointer"
            >
              <i className="ri-facebook-fill text-lg"></i>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform cursor-pointer"
            >
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-110 transition-transform cursor-pointer"
            >
              <i className="ri-twitter-x-line text-lg"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Navegación */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <a className="flex items-center gap-3 cursor-pointer" href="/">
              <img 
                src={images?.['logo-main'] || 'https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/9ec598441a0c5e4bb295dc2ea34728b5.jpeg'}
                alt="PUREZA Logo"
                className="h-12 sm:h-16 w-auto object-contain"
              />
            </a>

            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a
                href="#inicio"
                className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base"
              >
                Inicio
              </a>
              <a
                href="#nosotros"
                className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base"
              >
                Nosotros
              </a>
              <a
                href="#servicios"
                className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base"
              >
                Servicios
              </a>
              <a
                href="#certificacion"
                className="px-4 xl:px-6 py-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base font-medium"
              >
                Invima
              </a>
              <a
                href="#contacto"
                className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base"
              >
                Contacto
              </a>
              <div className="flex items-center gap-3">
                <a
                  className="px-4 xl:px-6 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base"
                  href="/admin/login"
                >
                  Administración
                </a>
              </div>
            </div>

            {/* Mobile: solo campana + hamburguesa */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                className="text-gray-700 text-2xl cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
              </button>
            </div>
          </div>

          {/* Menú móvil */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t">
              <div className="flex flex-col gap-4">
                <a
                  href="#inicio"
                  className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Inicio
                </a>
                <a
                  href="#nosotros"
                  className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Nosotros
                </a>
                <a
                  href="#servicios"
                  className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Servicios
                </a>
                <a
                  href="#certificacion"
                  className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-green-500 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-center font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Invima
                </a>
                <a
                  href="#contacto"
                  className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contacto
                </a>
                <a
                  className="px-6 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-center"
                  href="/admin/login"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Administración
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section con Slider */}
      <section id="inicio" className="relative h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              alt={slide.title}
              className="w-full h-full object-cover object-center"
              src={images?.[slide.imageKey] || 'https://readdy.ai/api/search-image?query=Large%20transparent%20water%20bottle%20jug%20with%20blue%20cap%20and%20blue%20label%20featuring%20PUREZA%20branding%2C%20crystal%20clear%20purified%20water%20inside%2C%20professional%20product%20photography%20on%20clean%20white%20background%2C%20high%20quality%20commercial%20water%20dispenser%20bottle%20with%20condensation%20droplets&width=1920&height=800&seq=pureza-hero-bottle-1&orientation=landscape'}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white px-4 w-full">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
                  {slide.title}
                </h1>
                <p className="text-xl sm:text-2xl md:text-3xl font-light drop-shadow-lg">
                  {slide.subtitle}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Indicadores del slider */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`rounded-full transition-all cursor-pointer ${
                currentSlide === index ? 'w-6 sm:w-8 h-2 sm:h-3 bg-white' : 'w-2 sm:w-3 h-2 sm:h-3 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Sección Nosotros */}
      <section id="nosotros" className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
                ¡Agua pura y cristalina,
                <br />
                calidad que se siente!
              </h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                Somos PUREZA, una empresa líder en distribución de agua purificada el López de Micay - Cabecera. Nos especializamos en ofrecer agua de la más alta calidad para hogares, oficinas y empresas. Nuestro proceso de purificación garantiza agua 100% segura y deliciosa. Cada botellón es tratado con los más altos estándares de calidad e higiene.
              </p>
              <a
                href="/nosotros"
                className="inline-block px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-xl transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Conoce más
              </a>
            </div>
            <div>
              <img
                alt="Botellones PUREZA"
                className="w-full h-auto rounded-lg shadow-xl"
                src={images?.['about-main'] || 'https://readdy.ai/api/search-image?query=Multiple%20large%20transparent%20water%20bottle%20jugs%20with%20blue%20caps%20and%20PUREZA%20blue%20labels%20stacked%20together%2C%20crystal%20clear%20purified%20water%20bottles%2C%20professional%20product%20display%20on%20white%20background%2C%20commercial%20water%20dispenser%20bottles%20with%20condensation%2C%20high%20quality%20studio%20shot&width=600&height=400&seq=pureza-about-bottles&orientation=landscape'}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sección Confianza */}
      <section id="certificacion" className="py-12 sm:py-16 lg:py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-green-600 to-emerald-500 rounded-full flex items-center justify-center">
            <i className="ri-shield-check-line text-white text-4xl sm:text-5xl"></i>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
            ¡Confianza que se bebe!
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 mb-2 sm:mb-4 px-4">
            Nuestro agua purificada cuenta con autorización oficial del Instituto Nacional de Vigilancia de Medicamentos y Alimentos de Colombia
          </p>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6 sm:mb-8 px-4 max-w-3xl mx-auto">
            Estamos autorizados para fabricar, envasar y vender agua potable tratada bajo la marca PUREZA. Garantizamos calidad e inocuidad en todo el proceso, con los más altos estándares de purificación para proteger la salud de tu familia.
          </p>

          {/* Tarjeta de información del registro */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6 sm:mb-8 text-left max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-green-700 to-emerald-600 px-6 sm:px-8 py-4 sm:py-5">
              <h3 className="text-white text-lg sm:text-xl font-bold flex items-center gap-2">
                <i className="ri-file-shield-line"></i>
                Resolución No. 2025068892
              </h3>
              <p className="text-green-100 text-sm mt-1">Fecha: 29 de diciembre de 2025</p>
            </div>
            <div className="p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Número de Autorización</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">RSA-4313-2025</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Nombre del Producto</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">AGUA POTABLE TRATADA PUREZA</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Titular del Registro</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">URCA ALL SERVICES</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Marca Registrada</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">PUREZA</p>
                </div>
              </div>
              <div className="border-t pt-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dirección del Titular</p>
                <p className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Carrera 1 # 31-5</p>
                <p className="text-gray-600">López de Micay, Cauca, Colombia</p>
              </div>
            </div>
          </div>

          {/* Botón al PDF */}
          <a
            href="https://storage.readdy-site.link/project_files/1d30528c-60d8-474b-a7b5-036af303bc9e/c6a6d710-b976-4392-b20b-1f973aa5d9d5_invima.pdf?v=8e7ac9b8d62d0932ce70e210d790557d"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-xl transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base font-medium"
          >
            <i className="ri-file-list-3-line"></i>
            Ver documento INVIMA oficial
          </a>
        </div>
      </section>

      {/* Sección Video Proceso */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 sm:mb-12 text-white px-4">
            Nuestro proceso de purificación
          </h2>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl cursor-pointer group" onClick={() => setShowVideoModal(true)}>
            <img
              alt="Proceso de purificación de agua"
              className="w-full h-auto"
              src={images?.['video-background'] || 'https://readdy.ai/api/search-image?query=Real%20industrial%20water%20purification%20system%20with%20multi-stage%20reverse%20osmosis%20filtration%2C%20sediment%20and%20carbon%20filters%20in%20clean%20production%20facility%2C%20stainless%20steel%20water%20treatment%20equipment%20with%20visible%20pipelines%20and%20gauges%2C%20professional%20water%20purification%20plant%20interior%20showing%20actual%20filtration%20process%2C%20clear%20water%20flowing%20through%20transparent%20pipes%2C%20bright%20clean%20industrial%20environment%20with%20quality%20control%20monitoring&width=1920&height=600&seq=pureza-real-purification&orientation=landscape'}
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-play-fill text-blue-900 text-3xl sm:text-4xl ml-1"></i>
              </div>
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent py-4 sm:py-6">
              <p className="text-white text-sm sm:text-base font-medium px-4">Haz clic para conocer nuestro proceso paso a paso</p>
            </div>
          </div>
        </div>
      </section>

      {/* Características Principales */}
      <section id="servicios" className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-10 sm:mb-16 text-center bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent px-4">
            Nuestros Valores
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-drop-line text-white text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-gray-800">Pureza Total</h3>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-green-600 to-emerald-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-shield-check-line text-white text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-gray-800">Calidad Certificada</h3>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-rose-600 to-pink-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-truck-line text-white text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-gray-800">Entrega Rápida</h3>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4 bg-gradient-to-br from-cyan-600 to-teal-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-time-line text-white text-3xl sm:text-4xl"></i>
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-gray-800">Horario de Atención</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-2">Lun - Vie: 8AM-12PM / 2PM-6PM<br/>Sáb: 9AM-12PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Llamado a la Acción */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent px-4">
            Nuestros Botellones
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 mb-3 sm:mb-4 px-4">
            Contienen Agua purificada de la más alta calidad, directo a tu hogar
          </p>
          <p className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-12 px-4">
            Cada botellón PUREZA pasa por un riguroso proceso de purificación para garantizar agua 100% segura, cristalina y refrescante para tu familia
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <img
              alt="Botellones de agua PUREZA"
              className="max-w-full h-auto rounded-lg shadow-lg"
              src={images?.['clients-happy1'] || 'https://readdy.ai/api/search-image?query=Multiple%20large%20transparent%20water%20dispenser%20bottle%20jugs%20with%20blue%20caps%20and%20PUREZA%20blue%20labels%20stacked%20together%20on%20clean%20white%20background%2C%20crystal%20clear%20purified%20water%20bottles%2C%20professional%20product%20display%20photography%2C%20commercial%20bottled%20water%20packaging%20with%20condensation%20droplets%2C%20high%20quality%20studio%20shot&width=800&height=500&seq=pureza-bottles-display&orientation=landscape'}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="relative">
        <div className="w-full">
          <svg className="w-full h-auto" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#1e3a8a"/>
          </svg>
        </div>
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 sm:py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
              <div>
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                  <img 
                    src={images?.['logo-main'] || 'https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/9ec598441a0c5e4bb295dc2ea34728b5.jpeg'}
                    alt="PUREZA Logo"
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Contáctanos</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">¡Listos siempre para atenderte!</p>
                      <p className="text-base sm:text-lg">+57&nbsp;318 064 8369</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">¡Estamos interesados en tus pedidos!</p>
                      <p className="text-base sm:text-lg break-all">contacto@aguapureza.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">Dirección principal</p>
                      <p className="text-base sm:text-lg">Calle San Francisco - Malecón</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                  ¿Quieres recibir más
                  <br />
                  información de <strong>PUREZA?</strong>
                </h3>
                <form className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
                  <input
                    placeholder="Email"
                    required
                    className="flex-1 px-4 py-3 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-white text-sm"
                    type="email"
                  />
                  <button
                    type="submit"
                    className="px-6 sm:px-8 py-3 bg-white text-blue-900 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    Enviar
                  </button>
                </form>
                <h3 className="text-xl sm:text-2xl font-bold mb-4">Síguenos en redes</h3>
                <div className="flex gap-4">
                  <a
                    href="https://www.facebook.com/profile.php?id=61585733834974"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-blue-900 text-xl"></i>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-instagram-line text-blue-900 text-xl"></i>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-blue-900 text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/20 text-center">
              <p className="text-xs sm:text-sm">
                © 2025 PUREZA. Todos los derechos reservados. |{' '}
                <a
                  href="https://readdy.ai/?ref=logo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline cursor-pointer"
                >
                  Charles Urbano
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal de Proceso de Purificación */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => { setShowVideoModal(false); setCurrentStep(0); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center">
                  <i className="ri-drop-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Proceso de Purificación</h3>
                  <p className="text-xs text-gray-500">11 etapas de calidad garantizada</p>
                </div>
              </div>
              <button
                onClick={() => { setShowVideoModal(false); setCurrentStep(0); }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-8 sm:p-10 space-y-6">
              {/* Barra de progreso */}
              <div className="flex items-center gap-2 mb-2">
                {purificationSteps.map((_, index) => (
                  <div
                    key={index}
                    className={`flex-1 h-2.5 rounded-full transition-all duration-500 ${
                      index <= currentStep ? 'bg-gradient-to-r from-blue-900 to-blue-700' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* Paso actual */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-8 sm:p-10 text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-5 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center animate-pulse">
                  <i className={`${purificationSteps[currentStep].icon} text-white text-4xl sm:text-5xl`}></i>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-900/10 text-blue-900 rounded-full text-sm font-semibold mb-4">
                  <span>Paso {currentStep + 1} de {purificationSteps.length}</span>
                </div>
                <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  {purificationSteps[currentStep].title}
                </h4>
                <p className="text-gray-700 text-base sm:text-lg leading-relaxed max-w-lg mx-auto">
                  {purificationSteps[currentStep].description}
                </p>
              </div>

              {/* Indicadores de pasos - Stepper horizontal elegante */}
              <div className="w-full">
                <div className="flex items-center justify-between">
                  {purificationSteps.map((_, index) => (
                    <div key={index} className="flex items-center flex-1 last:flex-none">
                      {/* Círculo con número */}
                      <button
                        onClick={() => setCurrentStep(index)}
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 transition-all cursor-pointer ${
                          index === currentStep
                            ? 'bg-blue-900 text-white ring-2 ring-blue-200 ring-offset-1'
                            : index < currentStep
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {index < currentStep ? (
                          <i className="ri-check-line text-xs"></i>
                        ) : (
                          index + 1
                        )}
                      </button>
                      {/* Línea conectora */}
                      {index < purificationSteps.length - 1 && (
                        <div className={`flex-1 h-[2px] mx-1.5 sm:mx-2 min-w-[8px] sm:min-w-[12px] ${
                          index < currentStep ? 'bg-green-500' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                {/* Nombre del paso actual debajo del stepper */}
                <p className="text-center text-sm text-blue-900 font-semibold mt-4 sm:hidden">
                  Paso {currentStep + 1}: {purificationSteps[currentStep].title}
                </p>
              </div>

              {/* Botones de navegación */}
              <div className="flex gap-4 pt-3">
                <button
                  onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                  disabled={currentStep === 0}
                  className={`flex-1 py-3.5 rounded-xl font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentStep === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Anterior
                </button>
                <button
                  onClick={() => {
                    if (currentStep < purificationSteps.length - 1) {
                      setCurrentStep(currentStep + 1);
                    } else {
                      setShowVideoModal(false);
                      setCurrentStep(0);
                    }
                  }}
                  className="flex-1 py-3.5 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  {currentStep < purificationSteps.length - 1 ? (
                    <>Siguiente <i className="ri-arrow-right-line ml-1"></i></>
                  ) : (
                    <>Finalizar <i className="ri-check-line ml-1"></i></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Order Modal */}
      <WhatsAppOrderModal 
        isOpen={showOrderModal} 
        onClose={() => setShowOrderModal(false)} 
      />

      {/* Botón Flotante de Pedido */}
      <button
        onClick={() => setShowOrderModal(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-green-600 to-green-500 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform z-40 animate-pulse cursor-pointer"
        title="Hacer pedido"
      >
        <i className="ri-shopping-cart-line text-white text-2xl sm:text-3xl"></i>
      </button>
    </div>
  );
}